/** @format */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { ReactNode } from "react";

import type { Notification } from "@/api/notificationsApi";
import { NotificationItem } from "@/components/Header/NotificationItem";
import { faBell, faTriangleExclamation } from "@/utils/iconLibraryTreeShake";
import LoadingCircle from "@/utils/LoadingCircle";

export const Route = createFileRoute("/dev/notifications")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: NotificationsPreview,
});

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-slate-200 mb-3 border-b border-slate-700 pb-2">
        {title}
      </h2>
      {children}
    </div>
  );
}

function DropdownShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-slate-500 mb-2">{title}</p>
      <div className="w-96 bg-slate-900 rounded-lg border border-slate-700 shadow-lg overflow-hidden">
        {children}
      </div>
    </div>
  );
}

const now = new Date().toISOString();
const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
const tenMinAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();
const hourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();

function mockNotification(
  overrides: Partial<Notification> & { category: string; title: string; body: string }
): Notification {
  return {
    id: `mock-${overrides.category}-${Math.random().toString(36).slice(2, 8)}`,
    status: "sent",
    deliveryMethod: "websocket",
    createdAt: fiveMinAgo,
    sentAt: fiveMinAgo,
    readAt: null,
    data: {},
    ...overrides,
  };
}

const MOCK_NOTIFICATIONS: Notification[] = [
  mockNotification({
    category: "race_start",
    title: "Green Flag!",
    body: "The Daytona 500 is underway",
    createdAt: now,
    data: { runId: "mock-run-1" },
  }),
  mockNotification({
    category: "race_end",
    title: "Race Complete",
    body: "Kyle Larson wins the Daytona 500!",
    createdAt: fiveMinAgo,
    data: { runId: "mock-run-1" },
  }),
  mockNotification({
    category: "qualifying_start",
    title: "Qualifying Underway",
    body: "Cup Series qualifying has started at Talladega",
    createdAt: tenMinAgo,
  }),
  mockNotification({
    category: "qualifying_end",
    title: "Qualifying Complete",
    body: "Denny Hamlin takes pole at Talladega",
    createdAt: hourAgo,
  }),
  mockNotification({
    category: "race_incident",
    title: "Caution Flag",
    body: "Caution flag out on lap 45 — multi-car incident in Turn 2",
    data: { incidentType: "caution", runId: "mock-run-1" },
  }),
  mockNotification({
    category: "race_incident",
    title: "Red Flag",
    body: "Red flag — race stopped for weather at lap 120",
    data: { incidentType: "red_flag", runId: "mock-run-1" },
  }),
  mockNotification({
    category: "lead_change",
    title: "Lead Change",
    body: "Chase Elliott takes the lead from Martin Truex Jr. on lap 87",
    data: { runId: "mock-run-1" },
  }),
  mockNotification({
    category: "race_fastest_lap",
    title: "New Fastest Lap",
    body: "William Byron sets fastest lap: 29.432s on lap 156",
    data: { runId: "mock-run-1" },
  }),
  mockNotification({
    category: "favorite_driver_position",
    title: "Position Change",
    body: "Kyle Larson moved from P5 to P2 (+3 positions)",
    data: { runId: "mock-run-1" },
  }),
  mockNotification({
    category: "favorite_driver_incident",
    title: "Driver Incident",
    body: "Chase Elliott involved in incident on lap 45",
    data: { runId: "mock-run-1" },
  }),
  mockNotification({
    category: "favorite_driver_pit",
    title: "Pit Stop",
    body: "Kyle Larson pitting from P3 — 4 tires + fuel (12.8s)",
    data: { runId: "mock-run-1" },
  }),
  mockNotification({
    category: "schedule_reminder",
    title: "Race Tomorrow",
    body: "Daytona 500 starts tomorrow at 2:30 PM ET",
  }),
];

function NotificationsPreview() {
  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-slate-100 mb-2">Notifications Preview</h1>
      <p className="text-slate-400 mb-8 text-sm">
        Dev-only page showing all notification UI states and categories.
      </p>

      {/* All notification categories */}
      <Section title="Notification Items — All Categories">
        <div className="w-96 bg-slate-900 rounded-lg border border-slate-700 overflow-hidden">
          {MOCK_NOTIFICATIONS.map(notification => (
            <NotificationItem key={notification.id} notification={notification} />
          ))}
        </div>
      </Section>

      {/* Dropdown states */}
      <Section title="Notification Dropdown States">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Empty state */}
          <DropdownShell title="Empty (no notifications)">
            <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200">Notifications</h3>
            </div>
            <div className="px-4 py-12 text-center">
              <FontAwesomeIcon icon={faBell} className="text-slate-600 text-2xl mb-2" />
              <p className="text-sm text-slate-400">No notifications yet</p>
            </div>
          </DropdownShell>

          {/* With items */}
          <DropdownShell title="With notifications">
            <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200">Notifications</h3>
              <button
                type="button"
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                Mark all read
              </button>
            </div>
            {MOCK_NOTIFICATIONS.slice(0, 4).map(notification => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}
          </DropdownShell>

          {/* Loading state */}
          <DropdownShell title="Loading">
            <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200">Notifications</h3>
            </div>
            <div className="px-4 py-12 flex justify-center">
              <LoadingCircle className="h-8 w-8 animate-spin" />
            </div>
          </DropdownShell>
        </div>
      </Section>

      {/* Push permission states */}
      <Section title="Push Permission Banners">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Permission prompt */}
          <DropdownShell title="Push prompt (default)">
            <div className="border-b border-slate-600 bg-slate-800 px-4 py-3">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={faBell}
                  className="mt-0.5 text-orange-400 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200">
                    Enable push notifications
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Get race alerts even when this tab is closed.
                  </p>
                  <button
                    type="button"
                    className="mt-2 rounded bg-orange-500 hover:bg-orange-400 px-3 py-1 text-xs font-medium text-white"
                  >
                    Enable Notifications
                  </button>
                </div>
              </div>
            </div>
          </DropdownShell>

          {/* Permission granted but not subscribed */}
          <DropdownShell title="Granted, not subscribed">
            <div className="border-b border-slate-600 bg-slate-800 px-4 py-3">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={faBell}
                  className="mt-0.5 text-orange-400 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200">
                    Enable push on this browser
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Get race alerts even when this tab is closed.
                  </p>
                  <button
                    type="button"
                    className="mt-2 rounded bg-orange-500 hover:bg-orange-400 px-3 py-1 text-xs font-medium text-white"
                  >
                    Enable Notifications
                  </button>
                </div>
              </div>
            </div>
          </DropdownShell>

          {/* Permission denied */}
          <DropdownShell title="Permission denied (blocked)">
            <div className="border-b border-slate-600 bg-slate-800 px-4 py-3">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="mt-0.5 text-amber-400 shrink-0"
                />
                <div>
                  <p className="text-sm font-medium text-slate-200">
                    Notifications are blocked
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Update your browser settings to allow notifications from this site.
                  </p>
                </div>
              </div>
            </div>
          </DropdownShell>

          {/* Error state */}
          <DropdownShell title="Error state">
            <div className="border-b border-slate-600 bg-slate-800 px-4 py-3">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={faBell}
                  className="mt-0.5 text-orange-400 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200">
                    Enable push notifications
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Get race alerts even when this tab is closed.
                  </p>
                  <p className="text-xs text-red-400 mt-1">
                    Failed to subscribe: Service worker registration not found
                  </p>
                  <button
                    type="button"
                    className="mt-2 rounded bg-orange-500 hover:bg-orange-400 px-3 py-1 text-xs font-medium text-white"
                  >
                    Enable Notifications
                  </button>
                </div>
              </div>
            </div>
          </DropdownShell>
        </div>
      </Section>
    </div>
  );
}
